import {
  Button,
  Center,
  HStack,
  RadioGroup,
  Text,
  VStack,
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { AddButton } from 'src/components/AddButton'
import { toast } from 'react-toastify'
import { Loading, Radio } from 'semente-js'
import { Modal } from 'src/components/base/Modal'
import ChecklistEmptyState from './EmptyState'
import { CreateChecklistForm } from './Create'
import { Checklist, ChecklistWithQuestions } from 'src/types'
import ChecklistList from './List'
import ChecklistDetails from './Details'
import ChecklistService from 'src/services/checklist/checklist'
import Icon from 'src/components/base/Icon'
import { Props } from './types'

const ChecklistTab: React.FC<Props> = ({ project_id }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isAddTemplate, setIsAddTemplate] = useState(false)
  const [selectedTemplate, setSelectedTemplate] =
    useState<ChecklistWithQuestions>()
  const [checklistsTemplates, setChecklistsTemplates] = useState({
    isLoading: true,
    data: [] as ChecklistWithQuestions[],
  })
  const [checklists, setChecklists] = useState({
    isLoading: false,
    data: [] as undefined | ChecklistWithQuestions[],
  })

  const [currentChecklist, setCurrentChecklist] = useState<{
    checklist: ChecklistWithQuestions
    startWithCreate?: boolean
  }>()

  const [checklistFormData, setChecklistFormData] = useState<
    ChecklistWithQuestions | {} | undefined
  >()

  const refreshChecklists = useCallback(async () => {
    try {
      setChecklists({ isLoading: true, data: undefined })
      const data = await ChecklistService.getAll({ project_id })
      setChecklists({ isLoading: false, data })
    } catch (err) {
      toast.error('Ocorreu um erro ao buscar os checklists')
    }
  }, [project_id])

  useEffect(() => {
    refreshChecklists()
  }, [refreshChecklists])

  useEffect(() => {
    ChecklistService.getAll({}).then((data) => {
      setChecklistsTemplates({ isLoading: false, data })
    })
  }, [])

  const handleCloseAddTemplate = () => {
    setIsAddTemplate(false)
    setSelectedTemplate(undefined)
  }

  const handleCreateOrUpdateChecklist: SubmitHandler<
    ChecklistWithQuestions
  > = async (checklist) => {
    setIsLoading(true)
    const isUpdate = 'id' in checklist

    try {
      if (isUpdate && checklists.data) {
        await ChecklistService.update(checklist.id, checklist)

        if (currentChecklist?.checklist.id === checklist.id) {
          setCurrentChecklist({ checklist })
        }
      } else {
        const newChecklist = await ChecklistService.create({
          ...checklist,
          is_template: !project_id,
          project_id,
        })

        setChecklists({
          ...checklists,
          data: [...(checklists.data || []), newChecklist],
        })
        setCurrentChecklist({ checklist: newChecklist, startWithCreate: true })
      }

      setChecklistFormData(undefined)
    } catch (err) {
      toast.error(
        `Ocorreu um erro ao ${isUpdate ? 'Atualizar' : 'Criar'} seu checklist`
      )
    }
    setIsLoading(false)
  }

  const handleDeleteChecklist = async (id: Checklist['id']) => {
    const oldChecklists = checklists
    try {
      setCurrentChecklist(undefined)
      setChecklists({
        isLoading: false,
        data: checklists.data?.filter((checklist) => checklist.id !== id),
      })
      await ChecklistService.delete(id)

      toast.success('Checklist removido com sucesso!')
    } catch (err) {
      setChecklists(oldChecklists)
      toast.error('Ocorreu um erro ao remover o checklist')
    }
  }

  useEffect(() => {
    if (currentChecklist?.checklist && checklists.data) {
      const checklistIndex = checklists.data?.findIndex(
        ({ id }) => id === currentChecklist.checklist?.id
      )

      if (checklists.data[checklistIndex] !== currentChecklist.checklist) {
        checklists.data[checklistIndex] = currentChecklist.checklist
        setChecklists({ ...checklists, data: checklists.data })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChecklist])

  const handleAddTemplate = async () => {
    if (selectedTemplate && project_id) {
      handleCloseAddTemplate()
      setChecklists({ isLoading: true, data: undefined })
      await ChecklistService.duplicate(selectedTemplate.id, {
        project_id: project_id,
      })
      toast.success('Template adicionado com sucesso!')
      refreshChecklists()
    } else {
      handleCloseAddTemplate()
    }
  }

  return (
    <VStack flex={1} alignItems={'flex-start'} w={'100%'}>
      {currentChecklist ? (
        <ChecklistDetails
          {...currentChecklist}
          handleBack={() => setCurrentChecklist(undefined)}
          handleModifyQuestions={(questions) => {
            setCurrentChecklist({
              ...currentChecklist,
              checklist: { ...currentChecklist.checklist, questions },
            })
          }}
          handleDeleteChecklist={handleDeleteChecklist}
          handleUpdateChecklist={() =>
            setChecklistFormData(currentChecklist.checklist)
          }
        />
      ) : (
        <VStack w={'100%'} {...(project_id && { maxW: '558px' })}>
          <HStack justifyContent={'space-between'} width={'100%'} pb={'16px'}>
            <Text
              fontWeight={600}
              fontFamily={'Raleway'}
              fontSize={'RH_sm'}
              color={'Gray.$800'}
            >
              {project_id ? 'Checklists' : 'Templates de checklists'}
            </Text>

            <div className="flex items-center gap-2 h-5">
              {checklists.data && checklists.data.length >= 1 && (
                <Button onClick={() => setIsAddTemplate(true)} minH={'36px'}>
                  <HStack spacing={'8px'} alignItems={'center'}>
                    <Icon size={'14px'} name={'plus'} />
                    <Text fontSize={'L_sm'} fontWeight={500}>
                      Adicionar template
                    </Text>
                  </HStack>
                </Button>
              )}
              {checklists.data && checklists.data.length >= 1 && (
                <AddButton
                  label={`Adicionar novo checklist`}
                  onClick={() => setChecklistFormData({})}
                />
              )}
            </div>
          </HStack>

          {checklists.isLoading ? (
            <Center mt={'64px !important'} w={'100%'}>
              <Loading
                height="100"
                width="100"
                color="#408EC5"
                secondaryColor="#408EC5"
                radius="12.5"
                ariaLabel="mutating-dots-loading"
                visible={true}
              />
            </Center>
          ) : (
            <>
              {checklists.data && (
                <>
                  {checklists.data.length >= 1 ? (
                    <ChecklistList
                      checklists={checklists.data}
                      handleClickChecklist={(checklist) =>
                        setCurrentChecklist({ checklist })
                      }
                    />
                  ) : (
                    <ChecklistEmptyState
                      isTemplate={!project_id}
                      handleCreate={() => setChecklistFormData({})}
                    />
                  )}
                </>
              )}
            </>
          )}
        </VStack>
      )}

      <Modal
        title={
          currentChecklist?.checklist && 'id' in currentChecklist.checklist
            ? `Editar ${project_id ? 'checklist' : 'template de checklist'}`
            : `Criar ${project_id ? 'checklist' : 'template de checklist'}`
        }
        isOpen={!!checklistFormData}
        onClose={() => setChecklistFormData(undefined)}
      >
        {isLoading ? (
          <Center minH={'300px'} w={'100%'}>
            <Loading
              height="100"
              width="100"
              color="#408EC5"
              secondaryColor="#408EC5"
              radius="12.5"
              ariaLabel="mutating-dots-loading"
              visible={true}
            />
          </Center>
        ) : (
          <CreateChecklistForm
            handleSubmitChecklist={handleCreateOrUpdateChecklist}
            checklist={checklistFormData as ChecklistWithQuestions}
          />
        )}
      </Modal>

      <Modal
        title={'Adicionar template'}
        isOpen={!!isAddTemplate}
        onClose={handleCloseAddTemplate}
      >
        {checklistsTemplates.isLoading ? (
          <Center minH={'300px'} w={'100%'}>
            <Loading
              height="100"
              width="100"
              color="#408EC5"
              secondaryColor="#408EC5"
              radius="12.5"
              ariaLabel="mutating-dots-loading"
              visible={true}
            />
          </Center>
        ) : (
          <VStack px={6} alignItems={'flex-start'}>
            <RadioGroup
              defaultValue={selectedTemplate?.id}
              value={selectedTemplate?.id}
              display={'flex'}
              flexDirection={'column'}
              gap={3}
            >
              {checklistsTemplates.data.map((checklist) => (
                <Radio
                  key={checklist.id}
                  id={checklist.id}
                  name={checklist.name}
                  label={checklist.name}
                  isChecked={selectedTemplate?.id === checklist.id}
                  onChange={() => setSelectedTemplate(checklist)}
                />
              ))}
            </RadioGroup>

            <HStack my={4} w={'100%'}>
              <Button
                variant={'Secondary'}
                flex={1}
                onClick={handleCloseAddTemplate}
              >
                Cancelar
              </Button>
              <Button
                variant={'Primary'}
                isDisabled={!selectedTemplate}
                flex={1}
                onClick={handleAddTemplate}
              >
                Adicionar
              </Button>
            </HStack>
          </VStack>
        )}
      </Modal>
    </VStack>
  )
}

export default ChecklistTab
